import React, { useState, useEffect, useMemo } from 'react'
import { Table } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { client } from '../../utils'
import { PriceCell } from './PriceCell'
import { CatalogRow } from './CatalogRow'

const fmt = n => '$' + n.toFixed(2)

const HOVER_STYLE_ID = 'cli-hover-styles'

const ensureHoverStyles = () => {
  if (document.getElementById(HOVER_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = HOVER_STYLE_ID
  style.textContent = `
    .cli-row:hover .cli-edit-btn { opacity: 1 !important; }
    .cli-catalog-row:hover { background: #eff6ff !important; }
  `
  document.head.appendChild(style)
}

export const App = ({ coursenum, initialLineItems, catalog, initialCourseFee }) => {
  const [lineItems, setLineItems] = useState(initialLineItems)
  const [, setCourseFee] = useState(initialCourseFee)
  const [editingPk, setEditingPk] = useState(null)
  const [editPrice, setEditPrice] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedFlash, setSavedFlash] = useState(false)

  useEffect(() => { ensureHoverStyles() }, [])

  const showPreviousCourseFee = initialLineItems.length === 0

  const total = lineItems.reduce((sum, li) => sum + li.effectivePrice, 0)
  const availableCatalog = useMemo(
    () => {
      const activeTypeIds = new Set(lineItems.map(li => li.lineItemTypeId))
      return catalog.filter(c => !activeTypeIds.has(c.id))
    },
    [catalog, lineItems]
  )

  const handleResponse = data => {
    setLineItems(data.lineItems)
    setCourseFee(data.courseFee)
    setError(null)
  }

  const flash = () => {
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 1800)
  }

  const addLineItem = async catalogItemId => {
    setSaving(true)
    try {
      const url = `/instructor/admin_setup.add_course_line_item/${coursenum}/${catalogItemId}/`
      const response = await client.post(url)
      handleResponse(response.data)
      flash()
    } catch (err) {
      setError('Failed to add line item.')
    } finally {
      setSaving(false)
    }
  }

  const removeLineItem = async pk => {
    setSaving(true)
    try {
      const url = `/instructor/admin_setup.remove_course_line_item/${coursenum}/${pk}/`
      const response = await client.post(url)
      handleResponse(response.data)
      flash()
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to remove line item.'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const startEditing = item => {
    setEditingPk(item.pk)
    setEditPrice(item.effectivePrice.toFixed(2))
  }

  const cancelEditing = () => {
    setEditingPk(null)
    setEditPrice('')
  }

  const savePrice = async item => {
    setSaving(true)
    try {
      const priceVal = parseFloat(editPrice)
      const urlPrice = Math.abs(priceVal - item.defaultPrice) < 0.005 ? 'null' : priceVal.toFixed(2)
      const url = `/instructor/admin_setup.update_course_line_item/${coursenum}/${item.pk}/${urlPrice}/`
      const response = await client.post(url)
      handleResponse(response.data)
      setEditingPk(null)
      setEditPrice('')
      flash()
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update price.'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="mb-0">
          Course Pricing
          <span className="text-muted font-weight-normal ml-1" style={{ fontSize: '12px' }}>
            ({lineItems.length} item{lineItems.length !== 1 ? 's' : ''})
          </span>
        </h3>
        {savedFlash && (
          <span className="d-flex align-items-center text-success font-weight-bold" style={{ fontSize: '12px', gap: '4px' }}>
            <i className="fa-regular fa-check-circle" /> Saved
          </span>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-myed alert-danger-myed" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {showPreviousCourseFee && (
        <div className="d-flex justify-content-between align-items-center border rounded mb-3 p-3" style={{ background: '#f9fafb' }}>
          <span className="text-muted" style={{ fontSize: '13px' }}>Previous course fee</span>
          <span className="font-weight-bold" style={{ fontSize: '16px' }}>{fmt(initialCourseFee)}</span>
        </div>
      )}

      <Table striped bordered className="table-sm" hover>
        <thead>
          <tr>
            <th>Line item</th>
            <th>Price</th>
            <th className="text-center" style={{ width: '100px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.length > 0 ? (
            lineItems.map(item => (
              <tr key={item.pk} className="cli-row">
                <td className="font-weight-bold">
                  {item.lineItemTypeName}
                </td>
                <td>
                  <PriceCell
                    item={item}
                    editing={editingPk === item.pk}
                    editPrice={editPrice}
                    setEditPrice={setEditPrice}
                    saving={saving}
                    onStartEdit={() => startEditing(item)}
                    onSave={() => savePrice(item)}
                    onCancel={cancelEditing}
                  />
                </td>
                <td className="text-center">
                  {item.required ? (
                    <span
                      title="Standard item — required on all courses and cannot be removed"
                      style={{ color: '#d1d5db', cursor: 'default' }}
                    >
                      <i className="fa-regular fa-lock" style={{ fontSize: '13px' }} />
                    </span>
                  ) : (
                    <i
                      className="fa-regular fa-trash-can text-danger"
                      title="Remove line item"
                      style={{ cursor: saving ? 'default' : 'pointer', fontSize: '14px' }}
                      onClick={saving ? undefined : () => removeLineItem(item.pk)}
                    />
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center text-muted p-4">
                No line items added yet. Add items from the catalog below.
              </td>
            </tr>
          )}
        </tbody>
        {lineItems.length > 0 && (
          <tfoot>
            <tr style={{ background: '#f9fafb' }}>
              <td className="text-muted font-weight-bold" style={{ fontSize: '12px', borderTop: '2px solid #e5e7eb' }}>
                Student-facing total (pre-tax)
              </td>
              <td className="font-weight-bold" style={{ fontSize: '14px', borderTop: '2px solid #e5e7eb' }}>
                {fmt(total)}
              </td>
              <td style={{ borderTop: '2px solid #e5e7eb' }}></td>
            </tr>
          </tfoot>
        )}
      </Table>

      {availableCatalog.length > 0 && (
        <div className="mt-3">
          <h4 className="font-weight-bold mb-3" style={{ fontSize: '14px' }}>Add from catalog</h4>
          <div className="d-flex flex-column" style={{ gap: '6px' }}>
            {availableCatalog.map(item => (
              <CatalogRow
                key={item.id}
                item={item}
                isAdded={false}
                isRequired={item.required}
                saving={saving}
                onAdd={addLineItem}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

App.propTypes = {
  coursenum: PropTypes.string.isRequired,
  initialLineItems: PropTypes.array.isRequired,
  catalog: PropTypes.array.isRequired,
  initialCourseFee: PropTypes.number.isRequired,
}
